import { Injectable } from '@angular/core'
import { NavigationEnd, NavigationStart, Router } from '@angular/router'
import { IonRouterOutlet, Platform } from '@ionic/angular'
import { Observable } from 'rxjs'
import { filter } from 'rxjs/operators'
import { ErrorType } from 'src/app/interfaces/exception'
import { RouterLoggingParams, SystemNavigateReason } from 'src/app/interfaces/log'
import { NavigateParams } from 'src/app/interfaces/router'
import { LogService } from 'src/app/services/log/log.service'
import { SnackbarService } from 'src/app/services/snackbar/snackbar.service'
import { isEmptyObject } from 'src/utils/utils'

export type NavigateType = 'user_action' | 'system'

@Injectable({
  providedIn: 'root',
})
export class RouterService {
  private routeHistory: string[] = []
  private backButtonClicked = false

  constructor(
    private platform: Platform,
    private router: Router,
    private logService: LogService,
    private snackbarService: SnackbarService
  ) {}

  /** generalService의 함수와 중복 */
  get isApp(): boolean {
    if (this.platform.is('desktop') || this.platform.is('mobileweb')) {
      return false
    } else {
      return true
    }
  }

  getRouteState<T>(): T | undefined {
    // const state = history.state ?? {}
    const state = this.router.getCurrentNavigation()?.extras.state
    return !state || isEmptyObject(state) ? undefined : (state as T)
  }

  addRouterHistoryTracker(): void {
    const initialUrl: URL = new URL(window.location.href)
    this.routeHistory.push(initialUrl.pathname)
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.navigationTrigger === 'popstate') {
          if (this.backButtonClicked) {
            this.backButtonClicked = false
          } else {
            //means it's a back navigation
            this.backwardNavigationLogging('button_click', 'user_action', undefined, 1, event.url)
          }
        }
        // 기본 정보 제외하고 전부 초기화
        this.logService.clearLogState()
        this.routeHistory.push(event.url)
      }
    })
  }

  getRouterSubject(): Observable<any> {
    return this.router.events.pipe(filter((event) => event instanceof NavigationEnd))
  }

  getCurrentUrl(): string {
    return this.routeHistory[this.routeHistory.length - 1]
  }

  getPreviousUrl(): string | undefined {
    return this.routeHistory[this.routeHistory.length - 2]
  }

  getHistory(): string[] {
    return [...this.routeHistory]
  }

  isSameAsCurrentUrl(path: string): boolean {
    const currentUrl = this.getCurrentUrl()
    return currentUrl === `/${path}` || currentUrl === path
  }

  navigate(
    url: string,
    params?: NavigateParams,
    navigateType: NavigateType = 'user_action',
    loggingParams?: RouterLoggingParams
  ): void {
    this.forwardNavigationLogging(url, params, navigateType, loggingParams)
    this.router.navigate([url], { ...params })
  }

  navigateBack(
    routerOutlet?: IonRouterOutlet,
    navigateType: NavigateType = 'user_action',
    loggingParams?: RouterLoggingParams,
    backCount = 0
  ) {
    this.backButtonClicked = true
    this.backwardNavigationLogging('button_click', navigateType, loggingParams, backCount)

    if (!this.isApp || routerOutlet === undefined) {
      window.history.go(++backCount * -1)
      return
    }
    if (routerOutlet === undefined) {
      return
    }
  }

  navigateToErrorPage(statusCode: number | string): void {
    let errorTypeValue: ErrorType = ErrorType.ERROR_404
    switch (statusCode) {
      case 'SYSTEM_CHECK':
      case 503:
        errorTypeValue = ErrorType.SYSTEM_CHECK
        this.navigate('error', { replaceUrl: true, queryParams: { errorType: errorTypeValue } }, 'system', {
          reason: SystemNavigateReason.ERROR,
        })
        break
      case 404:
        errorTypeValue = ErrorType.ERROR_404
        this.snackbarService.openSnackBar(SnackbarService.ERROR_500)
        break
      default:
        // 이외 status는 모두 500으로 처리
        errorTypeValue = ErrorType.ERROR_500
        this.snackbarService.openSnackBar(SnackbarService.ERROR_500)
        break
    }
  }

  private forwardNavigationLogging(
    url: string,
    params?: NavigateParams,
    navigateType: NavigateType = 'user_action',
    loggingParams?: RouterLoggingParams
  ): void {
    const queryParamString =
      params?.queryParams !== undefined
        ? '?' +
          Object.keys(params.queryParams)
            .map((key) => `${key}=${params.queryParams![key]}`)
            .join('&')
        : ''
    this.logService.routerLogging({
      direction: 'forward',
      from: this.getCurrentUrl(),
      to: url + queryParamString,
      navigateType,
      ...loggingParams,
    })
  }

  private backwardNavigationLogging(
    backwardType: 'swipe_back' | 'button_click',
    navigateType: NavigateType = 'user_action',
    loggingParams?: RouterLoggingParams,
    backCount = 0,
    url?: string
  ): void {
    switch (backwardType) {
      case 'swipe_back':
        this.logService.routerLogging({
          direction: 'backward',
          from: this.getCurrentUrl(),
          to: url,
          backCount: 1,
          navigateType,
        })
        return
      case 'button_click':
        this.logService.routerLogging({
          direction: 'backward',
          from: this.getCurrentUrl(),
          backCount: backCount === 0 ? 1 : backCount,
          navigateType,
          ...loggingParams,
        })
    }
  }
}
